import {
	CatalogPriceDriftDirection,
	useApplyCatalogPriceToOfferTierMutation,
	type CatalogPriceDriftActionMode,
} from '@codezo/graphql';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { getMutationError } from '../../../lib/mutation-response';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import type { Pricing } from './types';

type Drift = NonNullable<Pricing['catalogPriceDrift']>;
type DriftAction = Drift['actions'][number];

/**
 * The catalogue cost moved since this tier was priced.
 *
 * A quote keeps its own copy of each price, so the client never sees a price
 * move behind their back; when the catalogue changes later the copy goes stale,
 * and this says so. Every word and every rule is the backend's: the badge shows
 * `label`, the detail shows `explanation` and `caveat`, and the two buttons are
 * `actions` exactly as they come — available or not, with a warning to confirm.
 * Null (under 2%, or no catalogue price) renders nothing.
 */
export function CatalogDriftBadge({
	drift,
	pricingId,
	onApplied,
}: {
	drift?: Drift | null;
	/** The tier's `OrderQuoteOfferPricing` id, which the fix is applied to. */
	pricingId: string;
	/** Refetch the offer: the badge disappears once the tier matches. */
	onApplied: () => void;
}) {
	const [open, setOpen] = useState(false);
	const [confirming, setConfirming] = useState<DriftAction | null>(null);
	const [apply, { loading }] = useApplyCatalogPriceToOfferTierMutation();

	if (!drift) return null;
	const up = drift.direction === CatalogPriceDriftDirection.Up;
	const Arrow = up ? ArrowUpRight : ArrowDownRight;

	const run = async (mode: CatalogPriceDriftActionMode) => {
		try {
			const { data } = await apply({
				variables: { input: { orderQuoteOfferPricingId: pricingId, mode } },
			});
			const error = getMutationError(data?.applyCatalogPriceToOfferTier);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success(data?.applyCatalogPriceToOfferTier.message || 'Tier updated');
			setOpen(false);
			setConfirming(null);
			onApplied();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update the tier');
		}
	};

	/* A warning (the client has already seen the offer) is confirmed first;
	   otherwise the button does what its label says straight away. */
	const choose = (action: DriftAction) => {
		if (action.warning) {
			setOpen(false);
			setConfirming(action);
		} else {
			void run(action.mode);
		}
	};

	return (
		<>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					className={cn(
						'inline-flex w-fit items-center gap-1 rounded-full border px-2 py-px text-2xs font-medium transition-colors',
						up
							? 'border-status-warn/30 bg-status-warn-soft text-status-warn hover:bg-status-warn/20'
							: 'border-status-info/30 bg-status-info-soft text-status-info hover:bg-status-info/20'
					)}
				>
					<Arrow size={10} />
					{drift.label}
				</PopoverTrigger>
				<PopoverContent align="start" sideOffset={6} className="w-[26rem] gap-0 p-0">
					<div className="flex flex-col gap-2 px-4 py-3.5">
						<span
							className={cn(
								'inline-flex items-center gap-1.5 text-sm font-medium',
								up ? 'text-status-warn' : 'text-status-info'
							)}
						>
							<Arrow size={12} />
							{drift.label}
						</span>
						<p className="text-sm leading-relaxed text-text-2">{drift.explanation}</p>
						{drift.caveat && (
							<p className="text-xs leading-relaxed text-text-3">{drift.caveat}</p>
						)}
					</div>
					<div className="flex flex-col gap-2 border-t border-line-1 bg-surface-fill px-4 pb-4 pt-3">
						{drift.actions.map((action, i) => (
							<div key={action.mode} className="flex flex-col gap-1">
								<button
									type="button"
									disabled={!action.available || loading}
									onClick={() => choose(action)}
									className={cn(
										'h-9 rounded-md px-3 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-45',
										// The first is the main one, as the backend orders them.
										i === 0
											? 'bg-accent font-medium text-accent-fg hover:bg-accent-hover'
											: 'border border-line-2 bg-surface text-text-1 hover:bg-surface-hover'
									)}
								>
									{action.label}
								</button>
								{!action.available && action.unavailableReason && (
									<span className="text-xs text-text-3">
										{action.unavailableReason}
									</span>
								)}
							</div>
						))}
					</div>
				</PopoverContent>
			</Popover>

			<ConfirmDialog
				open={!!confirming}
				onOpenChange={(o) => !o && setConfirming(null)}
				title="Update this tier?"
				description={
					<span className="flex flex-col gap-2">
						<span>{confirming?.warning}</span>
						<span className="text-text-1">{confirming?.label}</span>
					</span>
				}
				confirmLabel="Update"
				variant="default"
				loading={loading}
				onConfirm={() => confirming && void run(confirming.mode)}
			/>
		</>
	);
}
